import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeService, setActiveService] = useState<number | null>(null);
  const [showRegistroModal, setShowRegistroModal] = useState(false);
  const [registroStatus, setRegistroStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [infoStatus, setInfoStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [showNormativaModal, setShowNormativaModal] = useState(false);
  const [selectedNormativa, setSelectedNormativa] = useState('');
  const [normativaStatus, setNormativaStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleRegistroSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const nombreApellido = formData.get('nombre_apellido') as string;
    const dni = formData.get('dni') as string;
    const cuit = formData.get('cuit') as string;
    const email = formData.get('email') as string;
    const telefonoMovil = formData.get('telefono_movil') as string;

    if (!nombreApellido || !dni || !cuit || !email || !telefonoMovil) {
      alert('Por favor complete todos los campos');
      return;
    }

    setRegistroStatus('loading');

    try {
      // Guardar en Supabase
      const { error } = await supabase
        .from('registros')
        .insert([
          {
            nombre_apellido: nombreApellido,
            dni,
            cuit,
            email,
            telefono_movil: telefonoMovil,
          },
        ]);

      if (error) {
        console.error('Error al guardar registro:', error);
        setRegistroStatus('error');
        alert('Hubo un error al enviar el registro. Por favor intente nuevamente.');
      } else {
        setRegistroStatus('success');
        form.reset();
      }
    } catch (error) {
      console.error('Error:', error);
      setRegistroStatus('error');
      alert('Hubo un error al enviar el registro. Por favor intente nuevamente.');
    }
  };

  const handleInfoSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const nombre = formData.get('info_nombre') as string;
    const email = formData.get('email') as string;
    const mensaje = formData.get('info_mensaje') as string;

    if (!nombre || !email || !mensaje) return;
    if (mensaje.length > 500) return;

    setInfoStatus('loading');

    try {
      const response = await fetch('https://readdy.ai/api/form/d6bj4fmqd308q1a09300', {
        method: 'POST',
        body: new URLSearchParams(formData as any),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      if (response.ok) {
        // Abrir mailto para enviar directo al correo
        const subject = encodeURIComponent(`Consulta sobre: ${selectedService}`);
        const body = encodeURIComponent(`Nombre: ${nombre}\nEmail: ${email}\nServicio: ${selectedService}\n\nMensaje:\n${mensaje}`);
        window.open(`mailto:marchphyar@gmail.com?subject=${subject}&body=${body}`, '_blank');
        setInfoStatus('success');
        form.reset();
      } else {
        setInfoStatus('error');
      }
    } catch {
      setInfoStatus('error');
    }
  };

  const openInfoModal = (serviceName: string) => {
    setSelectedService(serviceName);
    setInfoStatus('idle');
    setShowInfoModal(true);
  };

  const openNormativaModal = (docName: string) => {
    setSelectedNormativa(docName);
    setNormativaStatus('idle');
    setShowNormativaModal(true);
  };

  const handleNormativaSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const nombre = formData.get('norm_nombre') as string;
    const emailVal = formData.get('email') as string;
    const mensaje = formData.get('norm_mensaje') as string;

    if (!nombre || !emailVal || !mensaje) return;
    if (mensaje.length > 500) return;

    setNormativaStatus('loading');

    try {
      const response = await fetch('https://readdy.ai/api/form/d6bj7v2tehdqnvnphvk0', {
        method: 'POST',
        body: new URLSearchParams(formData as any),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      if (response.ok) {
        const subject = encodeURIComponent(`Consulta sobre: ${selectedNormativa}`);
        const body = encodeURIComponent(`Nombre: ${nombre}\nEmail: ${emailVal}\nDocumento: ${selectedNormativa}\n\nConsulta:\n${mensaje}`);
        window.open(`mailto:marchphyar@gmail.com?subject=${subject}&body=${body}`, '_blank');
        setNormativaStatus('success');
        form.reset();
      } else {
        setNormativaStatus('error');
      }
    } catch {
      setNormativaStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Modal Consulta Normativas */}
      {showNormativaModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowNormativaModal(false)}
          ></div>
          <div className="relative bg-white rounded-3xl p-8 md:p-10 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowNormativaModal(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl text-gray-500"></i>
            </button>

            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-text-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2b4a]">Consulta sobre Normativa</h3>
              <p className="text-gray-600 text-sm mt-2">{selectedNormativa}</p>
            </div>

            {normativaStatus === 'success' ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-4xl text-green-600"></i>
                </div>
                <h4 className="text-xl font-bold text-[#1a2b4a] mb-2">¡Gracias por contactarnos!</h4>
                <p className="text-gray-600 mb-6">Su consulta fue enviada correctamente. Nos pondremos en contacto a la brevedad.</p>
                <button
                  onClick={() => {
                    setShowNormativaModal(false);
                    setNormativaStatus('idle');
                  }}
                  className="bg-[#d4af37] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#c19d2f] transition-all cursor-pointer whitespace-nowrap"
                >
                  Cerrar
                </button>
              </div>
            ) : (
              <form
                id="normativa-consulta-form"
                data-readdy-form
                onSubmit={handleNormativaSubmit}
                className="space-y-5"
              >
                <input type="hidden" name="documento" value={selectedNormativa} />

                <div>
                  <label htmlFor="norm_nombre" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nombre del Consultante
                  </label>
                  <input
                    type="text"
                    id="norm_nombre"
                    name="norm_nombre"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Su nombre completo"
                  />
                </div>

                <div>
                  <label htmlFor="norm_email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Correo Electrónico
                  </label>
                  <input
                    type="email"
                    id="norm_email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="su@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="norm_mensaje" className="block text-sm font-semibold text-gray-700 mb-2">
                    Escriba su consulta
                  </label>
                  <textarea
                    id="norm_mensaje"
                    name="norm_mensaje"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm resize-none"
                    placeholder="Detalle su consulta aquí..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Máximo 500 caracteres</p>
                </div>

                {normativaStatus === 'error' && (
                  <p className="text-red-500 text-sm text-center">Hubo un error. Por favor intente nuevamente.</p>
                )}

                <button
                  type="submit"
                  disabled={normativaStatus === 'loading'}
                  className="w-full bg-[#d4af37] text-white py-4 rounded-full font-semibold text-lg hover:bg-[#c19d2f] transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {normativaStatus === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      Enviar Consulta
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Modal Más Información */}
      {showInfoModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowInfoModal(false)}
          ></div>
          <div className="relative bg-white rounded-3xl p-8 md:p-10 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowInfoModal(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl text-gray-500"></i>
            </button>

            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-send-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2b4a]">Más Información</h3>
              <p className="text-gray-600 text-sm mt-2">{selectedService}</p>
            </div>

            {infoStatus === 'success' ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-4xl text-green-600"></i>
                </div>
                <h4 className="text-xl font-bold text-[#1a2b4a] mb-2">¡Consulta Enviada!</h4>
                <p className="text-gray-600 mb-6">Su mensaje fue enviado a nuestro correo. Nos pondremos en contacto pronto.</p>
                <button
                  onClick={() => {
                    setShowInfoModal(false);
                    setInfoStatus('idle');
                  }}
                  className="bg-[#d4af37] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#c19d2f] transition-all cursor-pointer whitespace-nowrap"
                >
                  Cerrar
                </button>
              </div>
            ) : (
              <form
                id="info-servicio-form"
                data-readdy-form
                onSubmit={handleInfoSubmit}
                className="space-y-5"
              >
                <input type="hidden" name="servicio" value={selectedService} />

                <div>
                  <label htmlFor="info_nombre" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nombre Completo
                  </label>
                  <input
                    type="text"
                    id="info_nombre"
                    name="info_nombre"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Su nombre"
                  />
                </div>

                <div>
                  <label htmlFor="info_email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Correo Electrónico
                  </label>
                  <input
                    type="email"
                    id="info_email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="su@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="info_mensaje" className="block text-sm font-semibold text-gray-700 mb-2">
                    ¿Qué desea saber?
                  </label>
                  <textarea
                    id="info_mensaje"
                    name="info_mensaje"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm resize-none"
                    placeholder="Escriba su consulta aquí..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Máximo 500 caracteres</p>
                </div>

                {infoStatus === 'error' && (
                  <p className="text-red-500 text-sm text-center">Hubo un error. Por favor intente nuevamente.</p>
                )}

                <button
                  type="submit"
                  disabled={infoStatus === 'loading'}
                  className="w-full bg-[#d4af37] text-white py-4 rounded-full font-semibold text-lg hover:bg-[#c19d2f] transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {infoStatus === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line"></i>
                      Enviar Consulta
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Modal de Registro */}
      {showRegistroModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowRegistroModal(false)}
          ></div>
          <div className="relative bg-white rounded-3xl p-8 md:p-10 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <button 
              onClick={() => setShowRegistroModal(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl text-gray-500"></i>
            </button>

            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-add-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2b4a]">Registro Rápido</h3>
              <p className="text-gray-600 text-sm mt-2">Complete sus datos para iniciar el proceso</p>
            </div>

            {registroStatus === 'success' ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-4xl text-green-600"></i>
                </div>
                <h4 className="text-xl font-bold text-[#1a2b4a] mb-2">¡Registro Enviado!</h4>
                <p className="text-gray-600 mb-6">Nos pondremos en contacto con usted pronto.</p>
                <button
                  onClick={() => {
                    setShowRegistroModal(false);
                    setRegistroStatus('idle');
                  }}
                  className="bg-[#d4af37] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#c19d2f] transition-all cursor-pointer whitespace-nowrap"
                >
                  Cerrar
                </button>
              </div>
            ) : (
              <form
                id="registro-rapido-form"
                onSubmit={handleRegistroSubmit}
                className="space-y-5"
              >
                <div>
                  <label htmlFor="nombre_apellido" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nombre y Apellido
                  </label>
                  <input
                    type="text"
                    id="nombre_apellido"
                    name="nombre_apellido"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Juan Pérez"
                  />
                </div>

                <div>
                  <label htmlFor="dni" className="block text-sm font-semibold text-gray-700 mb-2">
                    DNI
                  </label>
                  <input
                    type="text"
                    id="dni"
                    name="dni"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="12345678"
                  />
                </div>

                <div>
                  <label htmlFor="cuit" className="block text-sm font-semibold text-gray-700 mb-2">
                    CUIT
                  </label>
                  <input
                    type="text"
                    id="cuit"
                    name="cuit"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="20-12345678-9"
                  />
                </div>

                <div>
                  <label htmlFor="email_registro" className="block text-sm font-semibold text-gray-700 mb-2">
                    Correo Electrónico
                  </label>
                  <input
                    type="email"
                    id="email_registro"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="juan@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="telefono_movil" className="block text-sm font-semibold text-gray-700 mb-2">
                    Teléfono Móvil
                  </label>
                  <input
                    type="tel"
                    id="telefono_movil"
                    name="telefono_movil"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="+54 11 1234-5678"
                  />
                </div>

                <button
                  type="submit"
                  disabled={registroStatus === 'loading'}
                  className="w-full bg-[#d4af37] text-white py-4 rounded-full font-semibold text-lg hover:bg-[#c19d2f] transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {registroStatus === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      Enviar Registro
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/10 backdrop-blur-md'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://public.readdy.ai/ai/img_res/6d08d071-f426-46a0-949f-17a3d0e98f3b.png" 
              alt="Logo Empresa Soluciones Financieras" 
              title="Soluciones Financieras Integrales"
              className="h-12 w-auto object-contain"
            />
            <span className={`text-2xl font-bold transition-colors ${
              isScrolled ? 'text-[#1a2b4a]' : 'text-white'
            }`}>
              marchpy s.a.
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('servicios')} className="text-sm font-medium hover:text-[#d4af37] transition-colors cursor-pointer whitespace-nowrap">
              Servicios
            </button>
            <button onClick={() => scrollToSection('sucursales')} className="text-sm font-medium hover:text-[#d4af37] transition-colors cursor-pointer whitespace-nowrap">
              Sucursales
            </button>
            <button onClick={() => scrollToSection('registro')} className="text-sm font-medium hover:text-[#d4af37] transition-colors cursor-pointer whitespace-nowrap">
              Registro
            </button>
            <button onClick={() => scrollToSection('terminos')} className="text-sm font-medium hover:text-[#d4af37] transition-colors cursor-pointer whitespace-nowrap">
              Términos
            </button>
            <button onClick={() => scrollToSection('contacto')} className="text-sm font-medium hover:text-[#d4af37] transition-colors cursor-pointer whitespace-nowrap">
              Contacto
            </button>
            <button className="px-5 py-2 border-2 border-[#d4af37] text-[#d4af37] rounded-full text-sm font-medium hover:bg-[#d4af37] hover:text-white transition-all cursor-pointer whitespace-nowrap flex items-center gap-2">
              <i className="ri-user-line"></i>
              Acceso Cliente
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/dafa20340721fd6f64bdca0833b80f06.jpg"
            alt="Ambiente corporativo moderno"
            title="Soluciones Financieras Profesionales"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-[55%]">
              <h1 className="text-white font-extrabold leading-tight">
                <div className="text-6xl md:text-7xl mb-2">SOLUCIONES</div>
                <div className="text-6xl md:text-7xl mb-2 ml-8">FINANCIERAS</div>
                <div className="text-6xl md:text-7xl ml-16">INTEGRALES</div>
              </h1>
            </div>
            
            <div className="md:w-[45%]">
              <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl">
                <p className="text-gray-700 text-base leading-relaxed mb-6">
                  Somos parte de un grupo empresarial consolidado, ofreciendo soluciones integrales de pagos y cobranzas con tecnología de vanguardia y cumplimiento normativo total.
                </p>
                <button 
                  onClick={() => scrollToSection('servicios')}
                  className="w-full bg-[#d4af37] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#c19d2f] transition-all flex items-center justify-center gap-3 cursor-pointer whitespace-nowrap"
                >
                  Conocer Servicios
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-8 mb-16">
            <h2 className="text-5xl font-extralight text-[#1a2b4a] md:w-[35%]">
              Nuestros Servicios Financieros
            </h2>
            <p className="text-gray-600 text-base md:w-[65%] flex items-end pb-2">
              Ofrecemos una amplia gama de soluciones financieras diseñadas para optimizar sus operaciones de pagos, cobranzas e importaciones con el más alto nivel de seguridad y cumplimiento normativo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-10 gap-8">
            <div 
              className="md:col-span-4 bg-white rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all duration-300"
              onMouseEnter={() => setActiveService(1)}
              onMouseLeave={() => setActiveService(null)}
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center mb-6 mx-auto">
                <i className="ri-exchange-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2b4a] text-center mb-4">Pay In / Pay Out SPAV</h3>
              <p className="text-gray-600 text-center leading-relaxed mb-6">
                Soluciones eficientes de pagos y cobros mediante nuestra Sociedad Proveedora de Acceso a Valores, garantizando transacciones seguras y rápidas para su negocio.
              </p>
              <button 
                onClick={() => openInfoModal('Pay In / Pay Out SPAV')}
                className="w-full border-2 border-[#1a2b4a] text-[#1a2b4a] py-3 rounded-full font-medium hover:bg-[#1a2b4a] hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                Más información
              </button>
            </div>

            <div 
              className="md:col-span-3 bg-white rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all duration-300"
              onMouseEnter={() => setActiveService(2)}
              onMouseLeave={() => setActiveService(null)}
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center mb-6 mx-auto">
                <i className="ri-file-list-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2b4a] text-center mb-4">Gestión de Cobranza</h3>
              <p className="text-gray-600 text-center leading-relaxed mb-6">
                Administración profesional y efectiva de sus cobranzas con procesos optimizados y seguimiento detallado para maximizar la recuperación de sus activos.
              </p>
              <button 
                onClick={() => openInfoModal('Gestión de Cobranza')}
                className="w-full border-2 border-[#1a2b4a] text-[#1a2b4a] py-3 rounded-full font-medium hover:bg-[#1a2b4a] hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                Más información
              </button>
            </div>

            <div 
              className="md:col-span-3 bg-white rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all duration-300"
              onMouseEnter={() => setActiveService(3)}
              onMouseLeave={() => setActiveService(null)}
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center mb-6 mx-auto">
                <i className="ri-bank-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2b4a] text-center mb-4">Asesoría de Pagos</h3>
              <p className="text-gray-600 text-center leading-relaxed mb-6">
                Consultoría especializada mediante cuentas recaudatorias y cuentas de pago de diferentes instituciones financieras para optimizar sus flujos de efectivo.
              </p>
              <button 
                onClick={() => openInfoModal('Asesoría de Pagos')}
                className="w-full border-2 border-[#1a2b4a] text-[#1a2b4a] py-3 rounded-full font-medium hover:bg-[#1a2b4a] hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                Más información
              </button>
            </div>

            <div 
              className="md:col-span-3 md:col-start-2 bg-white rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all duration-300"
              onMouseEnter={() => setActiveService(4)}
              onMouseLeave={() => setActiveService(null)}
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center mb-6 mx-auto">
                <i className="ri-line-chart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2b4a] text-center mb-4">Trabajo con ALyC&apos;s</h3>
              <p className="text-gray-600 text-center leading-relaxed mb-6">
                Colaboración estratégica con Agentes de Liquidación y Compensación para facilitar operaciones en el mercado de valores con total transparencia.
              </p>
              <button 
                onClick={() => openInfoModal('Trabajo con ALyC\'s')}
                className="w-full border-2 border-[#1a2b4a] text-[#1a2b4a] py-3 rounded-full font-medium hover:bg-[#1a2b4a] hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                Más información
              </button>
            </div>

            <div 
              className="md:col-span-3 bg-white rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all duration-300"
              onMouseEnter={() => setActiveService(5)}
              onMouseLeave={() => setActiveService(null)}
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center mb-6 mx-auto">
                <i className="ri-ship-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2b4a] text-center mb-4">Pagos de Importaciones</h3>
              <p className="text-gray-600 text-center leading-relaxed mb-6">
                Facilitamos y gestionamos pagos internacionales para sus importaciones con procesos ágiles y seguros que simplifican el comercio exterior.
              </p>
              <button 
                onClick={() => openInfoModal('Pagos de Importaciones')}
                className="w-full border-2 border-[#1a2b4a] text-[#1a2b4a] py-3 rounded-full font-medium hover:bg-[#1a2b4a] hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                Más información
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Branches Section */}
      <section id="sucursales" className="py-24 bg-[#f8f6f3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-2 h-2 rounded-full bg-[#d4af37]"></div>
              <span className="text-[#d4af37] font-semibold text-sm uppercase tracking-wider">Nuestra Red</span>
            </div>
            <h2 className="text-5xl font-bold text-[#1a2b4a]">
              Sucursales<br />Extrabancarias
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="h-64 w-full overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/d01fbc136c09b94607c70d66c62af391/df40825de441fc1099ec6249655e4388.jpeg"
                  alt="Sucursal Centro Buenos Aires - Lavalle 972"
                  title="Sucursal Centro Buenos Aires - Lavalle 972"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-8 text-center">
                <h3 className="text-2xl font-bold text-[#1a2b4a] mb-3">Centro de Buenos Aires</h3>
                <p className="text-gray-600 text-base mb-2">Lavalle 972</p>
                <p className="text-gray-500 text-sm mb-6">Lunes a Viernes: 9:00 - 18:00</p>
                <a 
                  href="https://www.google.com/maps/search/Lavalle+972+Buenos+Aires+Argentina" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="border-2 border-[#d4af37] text-[#d4af37] px-6 py-3 rounded-full font-medium hover:bg-[#d4af37] hover:text-white transition-all flex items-center justify-center gap-2 mx-auto cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-map-pin-line"></i>
                  Ver en mapa
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="h-64 w-full overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/d01fbc136c09b94607c70d66c62af391/3a621642818b6681922a5d313d07d8f3.jpeg"
                  alt="Sucursal Quilmes Centro - Lavalle 555"
                  title="Sucursal Quilmes Centro - Lavalle 555"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-8 text-center">
                <h3 className="text-2xl font-bold text-[#1a2b4a] mb-3">Quilmes Centro</h3>
                <p className="text-gray-600 text-base mb-2">Lavalle 555</p>
                <p className="text-gray-500 text-sm mb-6">Lunes a Viernes: 9:00 - 18:00</p>
                <a 
                  href="https://www.google.com/maps/search/Lavalle+555+Quilmes+Buenos+Aires+Argentina" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="border-2 border-[#d4af37] text-[#d4af37] px-6 py-3 rounded-full font-medium hover:bg-[#d4af37] hover:text-white transition-all flex items-center justify-center gap-2 mx-auto cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-map-pin-line"></i>
                  Ver en mapa
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="h-64 w-full overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/d01fbc136c09b94607c70d66c62af391/721a9c7010073a71412aa8521ff419cd.jpeg"
                  alt="Sucursal Hudson - Plaza comercial Hudson local 17"
                  title="Sucursal Hudson - Plaza comercial Hudson local 17"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-8 text-center">
                <h3 className="text-2xl font-bold text-[#1a2b4a] mb-3">Hudson</h3>
                <p className="text-gray-600 text-base mb-2">Plaza comercial Hudson local 17</p>
                <p className="text-gray-500 text-sm mb-6">Lunes a Viernes: 9:00 - 18:00</p>
                <a 
                  href="https://www.google.com/maps/search/Plaza+comercial+Hudson+local+17+Buenos+Aires+Argentina" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="border-2 border-[#d4af37] text-[#d4af37] px-6 py-3 rounded-full font-medium hover:bg-[#d4af37] hover:text-white transition-all flex items-center justify-center gap-2 mx-auto cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-map-pin-line"></i>
                  Ver en mapa
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Section */}
      <section id="registro" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
            <div className="md:col-span-2 bg-gradient-to-br from-[#1a2b4a] to-[#2a3b5a] rounded-l-3xl md:rounded-r-none rounded-r-3xl md:rounded-l-3xl p-12 text-white">
              <h2 className="text-4xl font-bold mb-6">Registro Rápido y Seguro</h2>
              <p className="text-white/90 leading-relaxed mb-6">
                Contamos con un proceso de registro manual eficiente y estamos implementando nuestro sistema automático para brindarle una experiencia aún más ágil.
              </p>
              <p className="text-white/80 leading-relaxed mb-8">
                Nuestro equipo está disponible para guiarlo en cada paso del proceso de registro, asegurando el cumplimiento de todas las normativas vigentes.
              </p>
              <button 
                onClick={() => setShowRegistroModal(true)}
                className="bg-[#d4af37] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#c19d2f] transition-all cursor-pointer whitespace-nowrap"
              >
                Iniciar Registro
              </button>
            </div>

            <div className="md:col-span-3 p-12 flex items-center justify-center">
              <div className="grid grid-cols-2 gap-8">
                <div className="flex flex-col items-center group cursor-pointer">
                  <div className="w-44 h-44 rounded-full border-3 border-[#d4af37] bg-white flex flex-col items-center justify-center hover:scale-105 transition-transform duration-300 shadow-lg">
                    <i className="ri-shield-check-line text-5xl text-[#d4af37] mb-2"></i>
                    <span className="text-[#1a2b4a] font-bold text-center text-sm">Registro UIF</span>
                  </div>
                </div>

                <div className="flex flex-col items-center group cursor-pointer">
                  <div className="w-44 h-44 rounded-full border-3 border-[#d4af37] bg-white flex flex-col items-center justify-center hover:scale-105 transition-transform duration-300 shadow-lg">
                    <i className="ri-user-settings-line text-5xl text-[#d4af37] mb-2"></i>
                    <span className="text-[#1a2b4a] font-bold text-center text-sm">Registro RENAPER</span>
                  </div>
                </div>

                <div className="flex flex-col items-center group cursor-pointer">
                  <div className="w-44 h-44 rounded-full border-3 border-[#d4af37] bg-white flex flex-col items-center justify-center hover:scale-105 transition-transform duration-300 shadow-lg">
                    <i className="ri-time-line text-5xl text-[#d4af37] mb-2"></i>
                    <span className="text-[#1a2b4a] font-bold text-center text-sm px-4">En trámite BCRA 2025</span>
                  </div>
                </div>

                <div className="flex flex-col items-center group cursor-pointer">
                  <div className="w-44 h-44 rounded-full border-3 border-[#d4af37] bg-white flex flex-col items-center justify-center hover:scale-105 transition-transform duration-300 shadow-lg">
                    <i className="ri-verified-badge-line text-5xl text-[#d4af37] mb-2"></i>
                    <span className="text-[#1a2b4a] font-bold text-center text-sm">Certificación de Seguridad</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Section */}
      <section id="terminos" className="py-24 bg-[#f8f6f3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1a2b4a] mb-4">Transparencia y Normativas</h2>
            <p className="text-gray-600 text-lg">Toda la documentación necesaria a su disposición</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white rounded-3xl p-12 border-l-6 border-[#d4af37] shadow-lg">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 rounded-full bg-[#d4af37]/10 flex items-center justify-center">
                  <i className="ri-file-text-line text-3xl text-[#d4af37]"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#1a2b4a]">Manuales de Procedimiento</h3>
              </div>
              
              <div className="space-y-4">
                <button onClick={() => openNormativaModal('Manual de Operaciones')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer group text-left">
                  <div className="flex items-center gap-3">
                    <i className="ri-file-pdf-line text-2xl text-red-500"></i>
                    <span className="text-gray-700 font-medium">Manual de Operaciones</span>
                  </div>
                  <i className="ri-mail-send-line text-xl text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </button>

                <button onClick={() => openNormativaModal('Procedimientos de Seguridad')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer group text-left">
                  <div className="flex items-center gap-3">
                    <i className="ri-file-pdf-line text-2xl text-red-500"></i>
                    <span className="text-gray-700 font-medium">Procedimientos de Seguridad</span>
                  </div>
                  <i className="ri-mail-send-line text-xl text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </button>

                <button onClick={() => openNormativaModal('Guía de Cumplimiento Normativo')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer group text-left">
                  <div className="flex items-center gap-3">
                    <i className="ri-file-pdf-line text-2xl text-red-500"></i>
                    <span className="text-gray-700 font-medium">Guía de Cumplimiento Normativo</span>
                  </div>
                  <i className="ri-mail-send-line text-xl text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </button>

                <button onClick={() => openNormativaModal('Manual de Prevención de Lavado')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer group text-left">
                  <div className="flex items-center gap-3">
                    <i className="ri-file-pdf-line text-2xl text-red-500"></i>
                    <span className="text-gray-700 font-medium">Manual de Prevención de Lavado</span>
                  </div>
                  <i className="ri-mail-send-line text-xl text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </button>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-12 border-r-6 border-[#d4af37] shadow-lg">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 rounded-full bg-[#d4af37]/10 flex items-center justify-center">
                  <i className="ri-article-line text-3xl text-[#d4af37]"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#1a2b4a]">Términos y Condiciones</h3>
              </div>
              
              <div className="space-y-4">
                <button onClick={() => openNormativaModal('Términos Generales de Servicio')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer group text-left">
                  <div className="flex items-center gap-3">
                    <i className="ri-file-list-line text-2xl text-[#1a2b4a]"></i>
                    <span className="text-gray-700 font-medium">Términos Generales de Servicio</span>
                  </div>
                  <i className="ri-mail-send-line text-xl text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </button>

                <button onClick={() => openNormativaModal('Política de Privacidad')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer group text-left">
                  <div className="flex items-center gap-3">
                    <i className="ri-file-list-line text-2xl text-[#1a2b4a]"></i>
                    <span className="text-gray-700 font-medium">Política de Privacidad</span>
                  </div>
                  <i className="ri-mail-send-line text-xl text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </button>

                <button onClick={() => openNormativaModal('Condiciones de Uso')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer group text-left">
                  <div className="flex items-center gap-3">
                    <i className="ri-file-list-line text-2xl text-[#1a2b4a]"></i>
                    <span className="text-gray-700 font-medium">Condiciones de Uso</span>
                  </div>
                  <i className="ri-mail-send-line text-xl text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </button>

                <button onClick={() => openNormativaModal('Política de Protección de Datos')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer group text-left">
                  <div className="flex items-center gap-3">
                    <i className="ri-file-list-line text-2xl text-[#1a2b4a]"></i>
                    <span className="text-gray-700 font-medium">Política de Protección de Datos</span>
                  </div>
                  <i className="ri-mail-send-line text-xl text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </button>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  <i className="ri-calendar-line mr-2"></i>
                  Última actualización: Enero 2025
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-[#faf8f5]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-2 h-2 rounded-full bg-[#d4af37]"></div>
            </div>
            <h2 className="text-5xl font-bold text-[#1a2b4a]">La Confianza de Nuestros Clientes</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-xl text-[#d4af37]"></i>
                ))}
              </div>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                "Excelente servicio de gestión de cobranzas. Han optimizado nuestros procesos y mejorado significativamente nuestros índices de recuperación."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center text-white font-bold text-xl">
                  MC
                </div>
                <div>
                  <p className="font-bold text-[#1a2b4a]">María Castillo</p>
                  <p className="text-sm text-gray-500">Directora Financiera - Retail</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-xl text-[#d4af37]"></i>
                ))}
              </div>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                "La asesoría en pagos internacionales ha sido fundamental para expandir nuestras operaciones de importación de manera segura y eficiente."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center text-white font-bold text-xl">
                  JR
                </div>
                <div>
                  <p className="font-bold text-[#1a2b4a]">Jorge Ramírez</p>
                  <p className="text-sm text-gray-500">CEO - Importadora</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-xl text-[#d4af37]"></i>
                ))}
              </div>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                "Profesionalismo y cumplimiento normativo impecable. Nos brindan la tranquilidad que necesitamos para operar en el mercado financiero."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center text-white font-bold text-xl">
                  LF
                </div>
                <div>
                  <p className="font-bold text-[#1a2b4a]">Laura Fernández</p>
                  <p className="text-sm text-gray-500">Gerente - ALyC</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 opacity-85">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-xl text-[#d4af37]"></i>
                ))}
              </div>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                "El sistema Pay In/Pay Out ha revolucionado nuestra operatoria. Transacciones rápidas, seguras y con total trazabilidad."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center text-white font-bold text-xl">
                  DP
                </div>
                <div>
                  <p className="font-bold text-[#1a2b4a]">Diego Pérez</p>
                  <p className="text-sm text-gray-500">CFO - Fintech</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 opacity-85">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-xl text-[#d4af37]"></i>
                ))}
              </div>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                "Atención personalizada y soluciones a medida. Han entendido perfectamente las necesidades de nuestra empresa y las han resuelto eficientemente."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center text-white font-bold text-xl">
                  SC
                </div>
                <div>
                  <p className="font-bold text-[#1a2b4a]">Sofía Campos</p>
                  <p className="text-sm text-gray-500">Directora - Comercio Exterior</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 opacity-85">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-xl text-[#d4af37]"></i>
                ))}
              </div>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                "La red de sucursales y el soporte técnico son excepcionales. Siempre disponibles para resolver cualquier consulta o inconveniente."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1a2b4a] to-[#d4af37] flex items-center justify-center text-white font-bold text-xl">
                  RM
                </div>
                <div>
                  <p className="font-bold text-[#1a2b4a]">Roberto Martínez</p>
                  <p className="text-sm text-gray-500">Gerente General - Pyme</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-[#1a2b4a] mb-4">Contáctenos</h2>
            <p className="text-gray-600 text-lg">Estamos aquí para ayudarle con sus necesidades financieras</p>
          </div>

          <form 
            id="contacto-form"
            data-readdy-form
            action="https://readdy.ai/api/form/d671aiiud6iuf6if12g0"
            method="POST"
            className="bg-white rounded-3xl p-10 shadow-xl border border-gray-100"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              
              // Validación
              const nombre = formData.get('nombre') as string;
              const email = formData.get('email') as string;
              const telefono = formData.get('telefono') as string;
              const mensaje = formData.get('mensaje') as string;

              if (!nombre || !email || !telefono || !mensaje) {
                alert('Por favor complete todos los campos');
                return;
              }

              if (mensaje.length > 500) {
                alert('El mensaje no puede exceder los 500 caracteres');
                return;
              }

              // Enviar formulario
              fetch(form.action, {
                method: 'POST',
                body: new URLSearchParams(formData as any),
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
              })
                .then(response => {
                  if (response.ok) {
                    alert('¡Mensaje enviado con éxito! Nos pondremos en contacto pronto.');
                    form.reset();
                  } else {
                    alert('Hubo un error al enviar el mensaje. Por favor intente nuevamente.');
                  }
                })
                .catch(() => {
                  alert('Hubo un error al enviar el mensaje. Por favor intente nuevamente.');
                });
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="nombre" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre Completo
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all"
                  placeholder="Juan Pérez"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all"
                  placeholder="juan@empresa.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="telefono" className="block text-sm font-semibold text-gray-700 mb-2">
                  Teléfono
                </label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all"
                  placeholder="+54 11 1234-5678"
                />
              </div>

              <div>
                <label htmlFor="servicio" className="block text-sm font-semibold text-gray-700 mb-2">
                  Servicio de Interés
                </label>
                <select
                  id="servicio"
                  name="servicio"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all cursor-pointer"
                >
                  <option value="Pay In/Pay Out SPAV">Pay In/Pay Out SPAV</option>
                  <option value="Gestión de Cobranza">Gestión de Cobranza</option>
                  <option value="Asesoría de Pagos">Asesoría de Pagos</option>
                  <option value="Trabajo con ALyC's">Trabajo con ALyC&apos;s</option>
                  <option value="Pagos de Importaciones">Pagos de Importaciones</option>
                  <option value="Consulta General">Consulta General</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="mensaje" className="block text-sm font-semibold text-gray-700 mb-2">
                Mensaje
              </label>
              <textarea
                id="mensaje"
                name="mensaje"
                required
                maxLength={500}
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all resize-none"
                placeholder="Cuéntenos cómo podemos ayudarle..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">Máximo 500 caracteres</p>
            </div>

            <button
              type="submit"
              className="w-full bg-[#d4af37] text-white py-4 rounded-full font-semibold text-lg hover:bg-[#c19d2f] transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              Enviar Mensaje
            </button>
          </form>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#1a2b4a] to-[#2a3b5a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#d4af37] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#d4af37] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-5xl font-extrabold text-white mb-6 leading-tight">
            Comience su Transformación<br />Financiera Hoy
          </h2>
          <p className="text-xl text-white/80 mb-10">
            Únase a las empresas que confían en nuestras soluciones
          </p>
          <button 
            onClick={() => setShowRegistroModal(true)}
            className="bg-[#d4af37] text-white px-12 py-5 rounded-full text-xl font-semibold hover:bg-[#c19d2f] transition-all shadow-2xl hover:shadow-3xl inline-flex items-center gap-3 cursor-pointer whitespace-nowrap"
          >
            Registrarse Ahora
            <i className="ri-arrow-right-line text-2xl"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a2b4a] text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <h4 className="text-lg font-bold mb-4">Servicios</h4>
              <div className="w-10 h-0.5 bg-[#d4af37] mb-6"></div>
              <ul className="space-y-4">
                <li>
                  <a href="#servicios" className="text-white/70 hover:text-white hover:translate-x-1 inline-block transition-all cursor-pointer">
                    Pay In/Pay Out SPAV
                  </a>
                </li>
                <li>
                  <a href="#servicios" className="text-white/70 hover:text-white hover:translate-x-1 inline-block transition-all cursor-pointer">
                    Gestión de Cobranza
                  </a>
                </li>
                <li>
                  <a href="#servicios" className="text-white/70 hover:text-white hover:translate-x-1 inline-block transition-all cursor-pointer">
                    Asesoría de Pagos
                  </a>
                </li>
                <li>
                  <a href="#servicios" className="text-white/70 hover:text-white hover:translate-x-1 inline-block transition-all cursor-pointer">
                    Trabajo con ALyC&apos;s
                  </a>
                </li>
                <li>
                  <a href="#servicios" className="text-white/70 hover:text-white hover:translate-x-1 inline-block transition-all cursor-pointer">
                    Pagos de Importaciones
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Empresa</h4>
              <div className="w-10 h-0.5 bg-[#d4af37] mb-6"></div>
              <ul className="space-y-4">
                <li>
                  <a href="#" className="text-white/70 hover:text-white hover:translate-x-1 inline-block transition-all cursor-pointer">
                    Sobre Nosotros
                  </a>
                </li>
                <li>
                  <a href="#sucursales" className="text-white/70 hover:text-white hover:translate-x-1 inline-block transition-all cursor-pointer">
                    Sucursales
                  </a>
                </li>
                <li>
                  <a href="#" className="text-white/70 hover:text-white hover:translate-x-1 inline-block transition-all cursor-pointer">
                    Nuestro Equipo
                  </a>
                </li>
                <li>
                  <a href="#" className="text-white/70 hover:text-white hover:translate-x-1 inline-block transition-all cursor-pointer">
                    Carreras
                  </a>
                </li>
                <li>
                  <Link to="/admin/login" className="text-white/70 hover:text-white hover:translate-x-1 inline-block transition-all cursor-pointer flex items-center gap-2">
                    <i className="ri-shield-keyhole-line"></i>
                    Uso Interno
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Legal</h4>
              <div className="w-10 h-0.5 bg-[#d4af37] mb-6"></div>
              <ul className="space-y-4">
                <li>
                  <a href="#terminos" className="text-white/70 hover:text-white hover:translate-x-1 inline-block transition-all cursor-pointer">
                    Términos y Condiciones
                  </a>
                </li>
                <li>
                  <a href="#terminos" className="text-white/70 hover:text-white hover:translate-x-1 inline-block transition-all cursor-pointer">
                    Política de Privacidad
                  </a>
                </li>
                <li>
                  <a href="#registro" className="text-white/70 hover:text-white hover:translate-x-1 inline-block transition-all cursor-pointer">
                    Cumplimiento Normativo
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Contacto</h4>
              <div className="w-10 h-0.5 bg-[#d4af37] mb-6"></div>
              <ul className="space-y-4">
                <li className="text-white/70">
                  <i className="ri-phone-line mr-2"></i>
                  +54 11 7660-5419
                </li>
                <li className="text-white/70">
                  <i className="ri-mail-line mr-2"></i>
                  marchphyar@gmail.com
                </li>
                <li className="text-white/70">
                  <i className="ri-time-line mr-2"></i>
                  Lun - Vie: 10:00 - 17:00
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="text-4xl font-extrabold mb-2">Soluciones Financieras</div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-0.5 bg-[#d4af37]"></div>
                <span className="text-sm text-white/60">Soluciones Financieras Integrales</span>
              </div>
            </div>

            <div className="text-center md:text-right">
              <p className="text-sm text-white/60 mb-3">© 2023 Soluciones Financieras. Todos los derechos reservados.</p>
              <div className="flex items-center justify-center md:justify-end gap-6">
                <a href="#" className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-[#d4af37] transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-2xl"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-[#d4af37] transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-2xl"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-[#d4af37] transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-2xl"></i>
                </a>
              </div>
              <div className="mt-3">
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-white/40 hover:text-[#d4af37] transition-colors">
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
